import React, {useCallback} from 'react';
import {PrimaryButton, SecondaryButton, SkeletonLoader} from 'components';
import {toast} from 'react-toastify';
import moment from 'moment';
import {getInitials} from 'helpers';

const CommentsSection = ({
  comments,
  isLoading,
  addCommentLoading,
  deleteCommentLoading,
  addUserComment,
  deleteUserComment,
  comment,
  setComment,
  commentId,
  setCommentId,
  next,
  handleLoadMore,
}) => {
  const handleAddComment = useCallback(() => {
    if (!comment.trim()) {
      return toast.error('Please enter a comment');
    }
    addUserComment({comment});
  }, [comment, addUserComment]);

  return (
    <div className="bg-white rounded p-4 flex items-start gap-1 w-full flex-col">
      <div className="flex items-center gap-1">
        <p className="text-[#080917] text-[10px] font-normal uppercase">
          Comments
        </p>
        <div className="flex items-center justify-center w-4 h-4 text-xs text-white rounded-full bg-[#0B1C56]">
          {comments?.comments?.length || 0}
        </div>
      </div>
      <div className="relative mt-4 w-full">
        {isLoading ? (
          <SkeletonLoader
            style={{width: '100%', height: 60, marginBottom: 10}}
          />
        ) : (
          <>
            <textarea
              onChange={e => setComment(e.target.value)}
              value={comment}
              name="comment"
              id="comment"
              className="w-full border-[0.5px] border-[#cccccc] rounded-lg p-2 text-sm"
              placeholder="Add a comment..."
            />
            <PrimaryButton
              buttonText={addCommentLoading ? 'Sending...' : 'Send'}
              fontSize="text-xs"
              xPadding="px-2"
              yPadding="pt-[9px] pb-[7px]"
              className="absolute right-2 bottom-4"
              onClick={handleAddComment}
              disabled={!comment.trim() || addCommentLoading}
            />
          </>
        )}
      </div>
      <div style={{opacity: addCommentLoading || isLoading ? 0.4 : 1}}>
        {isLoading ? (
          <>
            <SkeletonLoader
              style={{width: '100%', height: 40, marginBottom: 10}}
            />
            <SkeletonLoader
              style={{width: '100%', height: 40, marginBottom: 10}}
            />
          </>
        ) : (
          comments?.comments?.slice(0, next).map((comment, index, data) => (
            <div
              key={comment._id}
              className={`flex flex-col gap-1 mt-3 pb-2 ${
                index === data.length - 1 ? '' : 'border-b border-b-[#e9e9e9]'
              }`}
              onMouseEnter={() => setCommentId(comment._id)}
              onMouseLeave={() => setCommentId('')}
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-[#D9E5F8] text-[#2A5392] flex items-center justify-center rounded-full text-xs font-medium">
                  {getInitials(
                    `${comment?.author.first_name} ${comment?.author.last_name}`,
                  ) || '-'}
                </div>
                <p className="font-medium text-sm text-[#4A4A68]">
                  {`${comment?.author.first_name || '-'} ${
                    comment?.author.last_name || ''
                  }`}
                </p>
              </div>
              <p className="text-sm text-[#4A4A68]">
                {comment?.comment || '-'}
              </p>
              <div className="relative">
                <p className="text-[9px] text-[#B8B8B8] font-medium uppercase">
                  {moment(comment.createdAt).fromNow()}
                </p>
                {commentId === comment._id && (
                  <SecondaryButton
                    buttonText={
                      deleteCommentLoading && commentId === comment._id
                        ? 'Deleting...'
                        : 'Delete'
                    }
                    fontSize="text-[9px]"
                    btnHeight="h-7"
                    xPadding="px-2"
                    yPadding="pt-[4px] pb-[5px]"
                    className="absolute right-2 bottom-2 text-[#A13E3F]"
                    onClick={() => deleteUserComment(comment._id)}
                    disabled={deleteCommentLoading && commentId === comment._id}
                  />
                )}
              </div>
            </div>
          ))
        )}
      </div>
      {!isLoading && next < comments?.comments?.length && (
        <SecondaryButton
          className="flex items-start gap-2 mt-4"
          fontSize="text-xs"
          xPadding="px-2"
          yPadding="pt-[9px] pb-[7px]"
          btnHeight="h-7"
          buttonText="Load more"
          onClick={handleLoadMore}
        />
      )}
    </div>
  );
};

export default CommentsSection;
